import React, { useState, useRef, useEffect } from 'react';
import emailjs from '@emailjs/browser';

function Contact() {
  const form = useRef();
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Initialize EmailJS with your public key
    emailjs.init("qD5oPql94oqCD2o4z");
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    emailjs.sendForm(
      'gkmeena-portfolio', // your EmailJS service ID
      'gkmeena-portfolio', // your EmailJS template ID
      form.current
    )
      .then((result) => {
        setStatus({ 
          type: 'success', 
          message: 'Message sent successfully! I will get back to you soon.' 
        });
        form.current.reset();
      })
      .catch((error) => {
        setStatus({ 
          type: 'error', 
          message: 'Oops! Something went wrong. Please try again later.' 
        });
        console.error('EmailJS Error:', error);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <section id="contact" className="section min-h-[calc(100vh-4rem)] pt-16">
      {/* Space Decorative Elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Space Radio Dish */}
        <div className="absolute top-20 right-[5%] w-40 h-40 opacity-10">
          <div className="w-full h-full bg-gradient-to-br from-indigo-100 to-white rounded-full p-3 -rotate-12 shadow-lg">
            <div className="relative w-full h-full">
              {/* Dish Base */}
              <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-16 bg-gray-400"></div>
              {/* Dish */}
              <div className="absolute top-0 inset-x-0 h-3/4 bg-gradient-to-br from-gray-200 to-white rounded-t-full overflow-hidden">
                {/* Signal Waves Animation */}
                <div className="absolute inset-0 opacity-30">
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full border-4 border-indigo-400 rounded-full scale-0 animate-ping"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Space Message */}
        <div className="absolute bottom-24 left-[8%] w-32 h-24 opacity-10">
          <div className="w-full h-full bg-gradient-to-br from-purple-100 to-white rounded-lg p-2 rotate-6 shadow-lg animate-float-slow">
            <div className="relative w-full h-full bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 rounded-md">
              {/* Message Lines */}
              <div className="absolute inset-3 space-y-2">
                <div className="h-1 w-full bg-white/50 rounded"></div>
                <div className="h-1 w-3/4 bg-white/50 rounded"></div>
                <div className="h-1 w-1/2 bg-white/50 rounded"></div>
              </div>
              {/* Signal Indicator */}
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>

        {/* Signal Beams */}
        <div className="absolute top-1/3 left-[20%] space-y-8 opacity-5">
          <div className="w-32 h-0.5 bg-gradient-to-r from-indigo-400 to-transparent rounded-full animate-pulse"></div>
          <div className="w-24 h-0.5 bg-gradient-to-r from-purple-400 to-transparent rounded-full animate-pulse delay-100"></div>
          <div className="w-16 h-0.5 bg-gradient-to-r from-indigo-400 to-transparent rounded-full animate-pulse delay-200"></div>
        </div>
      </div>

      <div className="section-container h-full p-0">
        <p className="section-subtitle">Get In Touch</p>
        <h1 className="section-title mb-6">Contact Me</h1>
        <div className="container h-[calc(100%-8rem)]">
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-800">Let's talk about everything!</h2>
              <p className="text-gray-600">
                Have an idea? Let's build it together! Feel free to reach out using the contact form.
              </p>
              <div className="flex space-x-4">
                <a
                  href="https://github.com/gauravmeee"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-indigo-600 transition-colors"
                >
                  <svg
                    className="w-6 h-6"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
                      clipRule="evenodd"
                    />
                  </svg>
                </a>
                <a
                  href="https://www.linkedin.com/in/gaurav-meena-5b5b5b5b/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-indigo-600 transition-colors"
                >
                  <svg
                    className="w-6 h-6"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"
                      clipRule="evenodd"
                    />
                  </svg>
                </a>
              </div>
            </div>
            <form ref={form} onSubmit={handleSubmit} className="space-y-4 bg-white p-6 rounded-lg shadow-md border border-gray-100">
              {status.message && (
                <div className={`p-4 rounded-md ${status.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                  {status.message}
                </div>
              )}
              <div>
                <label htmlFor="user_name" className="block text-sm font-medium text-gray-700">
                  Name
                </label>
                <input
                  type="text"
                  id="user_name"
                  name="user_name"
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label htmlFor="user_email" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  type="email"
                  id="user_email"
                  name="user_email"
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={4}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                ></textarea>
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-2 px-4 rounded-md hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-300 ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Contact;